/** Pure helpers for ScriptArtifact → drag/drop / Quick Action →
 * `storyboard_generation` → ShotArtifact[] (VISTOERA_CANVAS_STORYBOARD_
 * GENERATION_NODE_SCRIPT_CONTEXT_AND_SHOT_ARTIFACT_V1). Mirrors
 * `scriptGenerationCanvas.ts` — plain field objects only; HTTP stays in
 * `CanvasBoard.tsx`. */

import type { Edge, Node } from '@xyflow/react'
import type {
  ScriptArtifact,
  StoryboardArtifact,
  StoryboardGenerationInputPayload,
  StoryboardGenerationScriptContext,
  StoryboardGenerationSupportingAssetRef,
} from '../../../api'
import type { FlowNodeData, ScriptContextDragPayload } from './types'
import { PCV_SCRIPT_CONTEXT_DND_MIME } from './canvasDragContract'
import { partitionScriptEntities } from './scriptGenerationCanvas'
import {
  assertStoryboardArtifactAcceptableForApply,
  hasValidStructuredScriptContext,
  reconcileStoryboardResultAfterContextChange,
} from './storyboardArtifactValidity'
import { enrichStoryboardShotsWithEntityBindings, resolveStoryboardEntityBindings } from './storyboardEntityBindings'
import {
  isProductionFormatComplete,
  productionFormatFingerprint,
  readProductionFormat,
} from './storyboardProductionFormat'

export {
  hasValidStructuredScriptContext,
  isValidShotArtifact,
  isValidStoryboardArtifact,
  resolveCurrentStoryboardPresentation,
  hasValidStoryboardForDownstream,
  demoteNonCurrentStoryboardResult,
  reconcileStoryboardResultAfterContextChange,
  storyboardGenerateReady,
} from './storyboardArtifactValidity'

/** Canvas edge linking a `script_generation` node to every
 * `storyboard_generation` node whose Primary Script Context it became.
 * Reuses `DERIVED_FROM` (same as Creative→Script). */
export const SCRIPT_CONTEXT_EDGE_RELATION = 'DERIVED_FROM'

/** Native DnD MIME for ScriptArtifact → Storyboard Primary Context.
 * Spec §18 — Quick Action and drag MUST use the same contract. */
export const SCRIPT_CONTEXT_DND_TYPE = PCV_SCRIPT_CONTEXT_DND_MIME

export interface StoryboardScriptContextField extends ScriptContextDragPayload {
  linkedAssetRefs: StoryboardSupportingAssetField[]
  linkedAt: string
  fingerprint: string
}

export interface StoryboardSupportingAssetField {
  asset_id?: string | null
  picker_item_id?: string | null
  asset_type: string
  display_name: string
  inherited?: boolean
  derivedFrom?: 'character_role' | 'product_role' | null
  source?: 'inherited' | 'manual'
}

export function computeScriptArtifactFingerprint(input: {
  script_id?: unknown
  title?: unknown
  logline?: unknown
  synopsis?: unknown
  target_duration_sec?: unknown
  characters?: unknown
  scenes?: unknown
  ending?: unknown
  tone?: unknown
  pacing?: unknown
}): string {
  const scenes = Array.isArray(input.scenes) ? input.scenes : []
  return JSON.stringify({
    script_id: String(input.script_id ?? ''),
    title: String(input.title ?? ''),
    logline: String(input.logline ?? ''),
    synopsis: String(input.synopsis ?? ''),
    target_duration_sec: input.target_duration_sec ?? null,
    characters: Array.isArray(input.characters) ? [...(input.characters as string[])].sort() : [],
    scene_count: scenes.length,
    scene_settings: scenes.map((s: { setting?: string; scene_number?: number }) => `${s.scene_number ?? ''}:${s.setting ?? ''}`),
    ending: String(input.ending ?? ''),
    tone: String(input.tone ?? ''),
    pacing: String(input.pacing ?? ''),
  })
}

export function buildScriptContextField(
  payload: ScriptContextDragPayload,
  supportingAssets: Array<Record<string, unknown>>,
): StoryboardScriptContextField {
  const linkedAssetRefs: StoryboardSupportingAssetField[] = (supportingAssets || [])
    .filter((a) => a && typeof a === 'object')
    .map((a) => ({
      asset_id: (a.asset_id as string | null | undefined) ?? null,
      picker_item_id: (a.picker_item_id as string | null | undefined) ?? null,
      asset_type: String(a.asset_type ?? 'unknown'),
      display_name: String(a.display_name ?? a.asset_id ?? 'asset'),
      inherited: true,
      derivedFrom: (a.derivedFrom as StoryboardSupportingAssetField['derivedFrom']) ?? null,
      source: 'inherited' as const,
    }))
  return {
    ...payload,
    linkedAssetRefs,
    linkedAt: new Date().toISOString(),
    fingerprint: computeScriptArtifactFingerprint(payload.script),
  }
}

function assetKey(a: StoryboardSupportingAssetField): string {
  return a.asset_id || `${a.asset_type}:${a.display_name}`
}

export function computeStoryboardParamsFingerprint(fields: Record<string, unknown>): string {
  const supportingAssets = Array.isArray(fields.supportingAssets) ? (fields.supportingAssets as StoryboardSupportingAssetField[]) : []
  const scriptContext = (fields.scriptContext as StoryboardScriptContextField | null) ?? null
  const productionFormat = readProductionFormat(fields)
  return JSON.stringify({
    script: scriptContext ? scriptContext.fingerprint : null,
    scriptSource: scriptContext ? scriptContext.sourceNodeId : null,
    assets: supportingAssets.map(assetKey).sort(),
    shotCountMode: fields.shotCountMode ?? 'AUTO',
    shotCount: fields.shotCountMode === 'CUSTOM' || fields.shotCountMode === 'EXPLICIT' ? fields.shotCount ?? null : 'AUTO',
    durationMode: fields.durationMode ?? 'AUTO',
    totalDurationSec: fields.durationMode === 'CUSTOM' || fields.durationMode === 'EXPLICIT' ? fields.totalDurationSec ?? null : 'AUTO',
    styleReference: fields.styleReference ?? 'AUTO',
    cameraLanguage: fields.cameraLanguage ?? 'AUTO',
    // Production Format Contract (aspect + resolution + version) — never AUTO final.
    productionFormat: productionFormatFingerprint(productionFormat),
    aspectRatio: productionFormat?.aspect_ratio ?? (fields.aspectRatio === 'AUTO' ? null : fields.aspectRatio ?? null),
    additionalInstructions: String(fields.additionalInstructions ?? ''),
    skill: fields.skillSelectionSource === 'EXPLICIT' ? fields.skillKey ?? null : 'AUTO',
    provider: fields.providerSelectionMode === 'EXPLICIT' ? fields.providerOptionId ?? null : 'DEFAULT',
  })
}

export function computeNeedsRegeneration(fields: Record<string, unknown>, scriptContextLiveFingerprint: string | undefined): boolean {
  if (!fields.storyboardResult) return false
  const paramsAtGen = (fields.paramsFingerprintAtGeneration as string | null) ?? null
  if (paramsAtGen == null) return false
  if (paramsAtGen !== computeStoryboardParamsFingerprint(fields)) return true
  const scriptContext = (fields.scriptContext as StoryboardScriptContextField | null) ?? null
  if (scriptContext && scriptContextLiveFingerprint && scriptContext.fingerprint !== scriptContextLiveFingerprint) return true
  return false
}

export function buildStoryboardInputPayload(fields: Record<string, unknown>): StoryboardGenerationInputPayload {
  const scriptContext = fields.scriptContext as StoryboardScriptContextField | null
  const supportingAssets = Array.isArray(fields.supportingAssets) ? (fields.supportingAssets as StoryboardSupportingAssetField[]) : []
  const script = scriptContext?.script
  const ctx: StoryboardGenerationScriptContext = {
    script_artifact_id: scriptContext?.scriptArtifactId ?? script?.script_id ?? null,
    script_id: script?.script_id ?? null,
    title: script?.title ?? '',
    logline: script?.logline ?? null,
    synopsis: script?.synopsis ?? '',
    target_duration_sec: script?.target_duration_sec ?? null,
    characters: script?.characters ?? null,
    key_objects: script?.key_objects ?? null,
    key_product_moments: script?.key_product_moments ?? null,
    tags: script?.tags ?? null,
    direction_summary: script?.direction_summary ?? null,
    scenes: script?.scenes ?? null,
    ending: script?.ending ?? null,
    tone: script?.tone ?? null,
    pacing: script?.pacing ?? null,
    source_run_id: scriptContext?.sourceRunId ?? null,
    fingerprint: scriptContext?.fingerprint ?? null,
    source_node_id: scriptContext?.sourceNodeId ?? null,
    version: scriptContext?.scriptVersion ?? null,
  }
  const assetRefs: StoryboardGenerationSupportingAssetRef[] = supportingAssets.map((a) => ({
    asset_id: a.asset_id ?? null,
    asset_type: a.asset_type,
    display_name: a.display_name,
  }))

  const shotCountMode = String(fields.shotCountMode ?? 'AUTO')
  const durationMode = String(fields.durationMode ?? 'AUTO')
  const shotCount =
    shotCountMode === 'AUTO' ? null : typeof fields.shotCount === 'number' ? (fields.shotCount as number) : null
  const totalDurationSec =
    durationMode === 'AUTO'
      ? null
      : typeof fields.totalDurationSec === 'number'
        ? (fields.totalDurationSec as number)
        : (script?.target_duration_sec ?? null)

  const productionFormat = readProductionFormat(fields)
  const aspectFromContract = productionFormat?.aspect_ratio ?? null
  const legacyAspect =
    fields.aspectRatio === 'AUTO' ? null : ((fields.aspectRatio as string | null) ?? null)

  return {
    script_context: ctx,
    supporting_assets: assetRefs.length ? assetRefs : null,
    shot_count: shotCount,
    total_duration_sec: totalDurationSec,
    style_reference: fields.styleReference === 'AUTO' ? null : ((fields.styleReference as string | null) ?? null),
    camera_language: fields.cameraLanguage === 'AUTO' ? null : ((fields.cameraLanguage as string | null) ?? null),
    aspect_ratio: aspectFromContract || legacyAspect,
    additional_instructions: (fields.additionalInstructions as string | null) || null,
  }
}

const MAX_GENERATION_HISTORY = 20

/** Canonical run bookkeeping on `storyboard_generation` node fields. */
export type StoryboardRunFields = {
  activeRunId: string | null
  runStatus: string | null
  runErrorMessage: string | null
}

export function readStoryboardRunFields(fields: Record<string, unknown>): StoryboardRunFields {
  const activeRunId = fields.activeRunId
  const runStatus = fields.runStatus
  const runErrorMessage = fields.runErrorMessage
  return {
    activeRunId: typeof activeRunId === 'string' ? activeRunId : null,
    runStatus: typeof runStatus === 'string' ? runStatus : null,
    runErrorMessage: typeof runErrorMessage === 'string' ? runErrorMessage : null,
  }
}

export function applyGeneratedStoryboard(
  fields: Record<string, unknown>,
  storyboard: StoryboardArtifact,
  runMeta: { runId: string; skillKey: string; skillVersion: string; providerOptionId: string | null },
): Record<string, unknown> {
  const check = assertStoryboardArtifactAcceptableForApply(storyboard)
  if (!check.ok) {
    // Do not write placeholder/refusal as current result.
    return {
      ...fields,
      activeRunId: null,
      runStatus: 'ERROR',
      runErrorMessage: check.message,
      lastRunId: runMeta.runId,
      lastRunSkillKey: runMeta.skillKey,
      lastRunSkillVersion: runMeta.skillVersion,
      lastRunProviderOptionId: runMeta.providerOptionId,
    }
  }
  const accepted = check.storyboard
  const productionFormat = readProductionFormat(fields)
  const entityBindings = resolveStoryboardEntityBindings({
    script: (fields.scriptContext as StoryboardScriptContextField | null)?.script ?? null,
    supportingAssets: Array.isArray(fields.supportingAssets)
      ? (fields.supportingAssets as StoryboardSupportingAssetField[])
      : [],
  })
  const stampedShots = enrichStoryboardShotsWithEntityBindings(accepted.shots || [], entityBindings)
  const stamped: typeof accepted = {
    ...accepted,
    aspect_ratio:
      (productionFormat && isProductionFormatComplete(productionFormat)
        ? productionFormat.aspect_ratio
        : accepted.aspect_ratio) || accepted.aspect_ratio,
    shots: stampedShots,
  }
  const history = Array.isArray(fields.generationHistory) ? (fields.generationHistory as unknown[]) : []
  const nextHistory = fields.storyboardResult
    ? [
        {
          storyboard: fields.storyboardResult,
          version: fields.storyboardResultVersion ?? 1,
          completedAt: new Date().toISOString(),
        },
        ...history,
      ].slice(0, MAX_GENERATION_HISTORY)
    : history
  return {
    ...fields,
    storyboardResult: stamped,
    storyboardResultVersion: ((fields.storyboardResultVersion as number) || 0) + 1,
    storyboardArtifactId: stamped.storyboard_artifact_id,
    generationHistory: nextHistory,
    // Snapshot of contract at generation — used for stale / mismatch checks.
    productionFormatAtGeneration: productionFormat,
    paramsFingerprintAtGeneration: computeStoryboardParamsFingerprint(fields),
    lastRunId: runMeta.runId,
    lastRunSkillKey: runMeta.skillKey,
    lastRunSkillVersion: runMeta.skillVersion,
    lastRunProviderOptionId: runMeta.providerOptionId,
  }
}

/** Immediate Diverge placeholder sibling (Canonical V3) — B/C before SUCCESS. */
export function buildStoryboardDivergePlaceholderFields(
  originFields: Record<string, unknown>,
  meta: {
    divergenceGroupId: string
    siblingIndex: number
    originNodeId: string
    runId: string
  },
): Record<string, unknown> {
  const {
    storyboardResult: _r,
    storyboardResultVersion: _v,
    storyboardArtifactId: _aid,
    generationHistory: _h,
    paramsFingerprintAtGeneration: _pf,
    activeRunId: _runId,
    runStatus: _runStatus,
    runErrorMessage: _runErr,
    activeDivergeRunId: _dRun,
    divergeRunStatus: _dStatus,
    divergeRunErrorMessage: _dErr,
    ...inheritable
  } = originFields
  return {
    ...inheritable,
    storyboardResult: null,
    storyboardResultVersion: 0,
    storyboardArtifactId: null,
    generationHistory: [],
    paramsFingerprintAtGeneration: null,
    activeRunId: meta.runId,
    runStatus: 'GENERATING',
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    divergenceGroupId: meta.divergenceGroupId,
    divergenceSiblingIndex: meta.siblingIndex,
    divergenceOriginNodeId: meta.originNodeId,
  }
}

export function buildStoryboardDivergeSiblingResultFields(
  originFields: Record<string, unknown>,
  storyboard: StoryboardArtifact,
  runMeta: {
    runId: string
    skillKey: string
    skillVersion: string
    providerOptionId: string | null
    divergenceGroupId: string
    siblingIndex: number
    originNodeId: string
  },
): Record<string, unknown> {
  const base = buildStoryboardDivergePlaceholderFields(originFields, {
    divergenceGroupId: runMeta.divergenceGroupId,
    siblingIndex: runMeta.siblingIndex,
    originNodeId: runMeta.originNodeId,
    runId: runMeta.runId,
  })
  return {
    ...applyGeneratedStoryboard(base, storyboard, runMeta),
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    divergenceGroupId: runMeta.divergenceGroupId,
    divergenceSiblingIndex: runMeta.siblingIndex,
    divergenceOriginNodeId: runMeta.originNodeId,
  }
}

export function findScriptContextSourceNode(nodes: Node[], edges: Edge[], storyboardNodeId: string): Node | null {
  const edge = edges.find(
    (e) =>
      e.target === storyboardNodeId &&
      (e.data as { relationType?: string } | undefined)?.relationType === SCRIPT_CONTEXT_EDGE_RELATION,
  )
  if (!edge) return null
  const source = nodes.find((n) => n.id === edge.source) ?? null
  if (!source) return null
  const data = source.data as unknown as FlowNodeData
  if (data.nodeType !== 'script_generation') return null
  return source
}

export function liveScriptContextFingerprint(nodes: Node[], edges: Edge[], storyboardNodeId: string): string | undefined {
  const source = findScriptContextSourceNode(nodes, edges, storyboardNodeId)
  if (!source) return undefined
  const data = source.data as unknown as FlowNodeData
  const fields = (data.fields || {}) as Record<string, unknown>
  const script = fields.scriptResult as ScriptArtifact | null
  if (!script) return undefined
  return computeScriptArtifactFingerprint(script)
}

export function buildScriptContextDragPayloadFromScriptNode(
  sourceNodeId: string,
  fields: Record<string, unknown>,
): ScriptContextDragPayload | null {
  const script = fields.scriptResult as ScriptArtifact | null
  if (!script) return null
  const supportingAssets = Array.isArray(fields.supportingAssets)
    ? (fields.supportingAssets as Array<{ asset_type?: string; display_name?: string }>)
    : []
  const { characters, keyObjects, characterReferenceByName } = partitionScriptEntities(script, supportingAssets)
  const normalized: ScriptArtifact = {
    ...script,
    characters,
    key_objects: keyObjects.length ? keyObjects : script.key_objects,
    entity_asset_bindings:
      script.entity_asset_bindings ||
      Object.entries(characterReferenceByName).map(([name, ref]) => ({
        entity_type: 'character',
        entity_name: name,
        entity_id: `character:${name}`,
        asset_id: String(ref.asset_id || ''),
        asset_display_name: ref.display_name || null,
        relationship: 'depicts',
        confidence: 'high',
        source: 'script_drag_partition',
      })).filter((b) => b.asset_id),
  }
  return {
    sourceNodeId,
    sourceRunId: (fields.lastRunId as string | null) ?? null,
    scriptArtifactId: (fields.scriptArtifactId as string | null) ?? script.script_id,
    scriptVersion: (fields.scriptResultVersion as number | null) ?? 1,
    assetsSourceNodeId: sourceNodeId,
    script: normalized,
  }
}

/** Clear Primary Script Context and drop only assets inherited from that Script edge. */
export function clearScriptContextOnUnlink(
  fields: Record<string, unknown>,
  sourceNodeId: string | null | undefined,
): Record<string, unknown> {
  const existing = Array.isArray(fields.supportingAssets)
    ? (fields.supportingAssets as StoryboardSupportingAssetField[])
    : []
  const nextAssets = existing.filter((a) => {
    if (!a.inherited) return true
    // Inherited refs without per-asset sourceNodeId were linked with the Script edge.
    void sourceNodeId
    return false
  })
  return reconcileStoryboardResultAfterContextChange({
    ...fields,
    scriptContext: null,
    supportingAssets: nextAssets,
  })
}

/**
 * Build the storyboard node fields bag with Primary Script Context already
 * baked in — used by Quick Action so create is atomic (node + binding + edge)
 * and never relies on a follow-up setNodes that React can eager-bail.
 */
export function buildStoryboardFieldsWithScriptContext(
  defaultFields: Record<string, unknown>,
  payload: ScriptContextDragPayload,
  scriptSupportingAssets: Array<Record<string, unknown>>,
): Record<string, unknown> {
  const contextField = buildScriptContextField(payload, scriptSupportingAssets)
  return reconcileStoryboardResultAfterContextChange({
    ...defaultFields,
    scriptContext: contextField,
    supportingAssets: contextField.linkedAssetRefs,
  })
}

/** Canonical FE precondition — same predicate as Generate button + submit. */
export function assertStoryboardGeneratePrecondition(fields: Record<string, unknown>): void {
  if (!hasValidStructuredScriptContext(fields.scriptContext as StoryboardScriptContextField | null)) {
    throw new Error('MISSING_PRIMARY_CONTEXT')
  }
}
